'use client'

import raf from 'raf'
import { ReactNode, useEffect, useRef } from 'react'

const computePageScrollPercentage = (): number => {
  if (typeof window === 'undefined') return 0
  const documentElement = document.documentElement
  const body = document.body
  const scrollTop = documentElement.scrollTop || body.scrollTop
  const scrollHeight = documentElement.scrollHeight || body.scrollHeight
  const clientHeight = documentElement.clientHeight
  return (scrollTop / (scrollHeight - clientHeight)) * 100
}

// Global flag to temporarily disable scroll tracking during programmatic scrolling
let isScrollingProgrammatically = false
let scrollPauseTimeout: NodeJS.Timeout | null = null

export const pauseScrollTracking = (duration = 1000) => {
  isScrollingProgrammatically = true
  if (scrollPauseTimeout) {
    clearTimeout(scrollPauseTimeout)
  }
  scrollPauseTimeout = setTimeout(() => {
    isScrollingProgrammatically = false
  }, duration)
}

interface PageScrollTrackerProps {
  percentageThreshold: number
  onScrollThresholdPassed: () => void
}

const PageScrollTracker = ({
  percentageThreshold,
  onScrollThresholdPassed,
}: PageScrollTrackerProps): ReactNode => {
  const tickingRef = useRef(false)

  const onPageScroll = () => {
    // Skip tracking during programmatic scrolling
    if (isScrollingProgrammatically) return

    if (!tickingRef.current) {
      raf(() => {
        const scrollPercentage = computePageScrollPercentage()
        if (scrollPercentage >= percentageThreshold) {
          onScrollThresholdPassed()
        }
        tickingRef.current = false
      })
      tickingRef.current = true
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      onPageScroll()
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [percentageThreshold, onScrollThresholdPassed])

  return null
}

export default PageScrollTracker
